import { useEffect, useState } from 'react';
import type { Conversacion, Mensaje, PlantillaMeta } from '../../api/ops/buzon';
import type { Operaciones } from '../../api/operaciones';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Icono } from '../../componentes/Icono';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { usePermisos } from '../../permisos/permisos';
import { textoDeError } from './errores';
import { CON_VENTANA, cuerpoDe, HUECO, huecosDe, nombreDe, primerNombre } from './reglas';

/**
 * «Escribir con una plantilla» (11.3): las plantillas aprobadas del canal (`listMetaTemplates` con `status=approved` y
 * `channel_id`; con `canales.buzon.atender` también, solo para ver: Encargado y Recepción la usan fuera de las 24 h, G686), sus huecos tal como los nombra Meta, la vista
 * previa y el consumo (G45, G525) sin la cifra del saldo (la API no la da a quien atiende; pregunta 193). Las de
 * marketing, apagadas si el cliente no aceptó comerciales por ese canal (sus permisos, de `getCustomer`). Sin saldo
 * (`409 no_balance`), aviso en rojo.
 */
const CATEGORIA: Readonly<Record<string, string>> = { utility: 'Utilidad', marketing: 'Marketing' };
const idioma = (l: string) => (l.startsWith('es') ? 'español' : l.startsWith('en') ? 'inglés' : l);
const deQuien = (p: PlantillaMeta) => (p.requested_by === 'entregas' || p.requested_by === 'platform' ? 'De Reservaria' : 'De tu negocio');

interface Props {
    conversacion: Conversacion;
    nombreCanal: string;
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    onCerrar: () => void;
    onEnviada: (m: Mensaje) => void;
    navegar: (ruta: string) => void;
}

export function EscribirConPlantilla({ conversacion: c, nombreCanal, ops, negocio, movil, onCerrar, onEnviada, navegar }: Props) {
    const avisar = useAvisoBreve();
    const { puede } = usePermisos();
    const [plantillas, setPlantillas] = useState<PlantillaMeta[] | null>(null);
    const [comercial, setComercial] = useState(false);
    const [elegida, setElegida] = useState<string | null>(null);
    const [valores, setValores] = useState<Record<string, Record<string, string>>>({});
    const [confirmar, setConfirmar] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [sinSaldo, setSinSaldo] = useState(false);
    const nombre = nombreDe(c);
    const primero = primerNombre(nombre);
    const cliente = c.contact.customer_id ?? null;

    useEffect(() => {
        let vivo = true;
        ops.listMetaTemplates(negocio, { channel_id: c.channel_id, status: 'approved', limit: 50 })
            .then((l) => { if (vivo) setPlantillas(l.data); })
            .catch(() => { if (vivo) setPlantillas([]); });
        return () => { vivo = false; };
    }, [ops, negocio, c.channel_id]);

    useEffect(() => {
        if (cliente === null) {
            return;
        }
        let vivo = true;
        ops.getCustomer(negocio, cliente)
            .then((r) => { if (vivo) setComercial((r.datos.consents ?? []).some((p) => p.channel === c.channel_type && p.purpose === 'marketing' && p.state === 'granted')); })
            .catch(() => undefined);
        return () => { vivo = false; };
    }, [ops, negocio, cliente, c.channel_type]);

    const plantilla = plantillas?.find((p) => p.id === elegida) ?? null;
    const huecos = plantilla === null ? [] : huecosDe(plantilla);
    const mios = plantilla === null ? {} : (valores[plantilla.id] ?? {});
    const falta = huecos.some((h) => (mios[h] ?? '').trim() === '');

    const enviar = () => {
        if (plantilla === null) {
            return;
        }
        setEnviando(true);
        const variables = Object.fromEntries(huecos.map((h) => [h, (mios[h] ?? '').trim()]));
        ops.replyToConversation(negocio, c.id, { template: { template_id: plantilla.id, variables } })
            .then((m) => {
                setConfirmar(false);
                onEnviada(m);
            })
            .catch((e: unknown) => {
                setConfirmar(false);
                if (e instanceof ErrorApi && e.estado === 409 && (e.problema as Record<string, unknown>).channel_reason === 'no_balance') {
                    setSinSaldo(true);
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido enviar la plantilla. Prueba de nuevo.') });
            })
            .finally(() => { setEnviando(false); });
    };

    const fuera = CON_VENTANA.includes(c.channel_type) && c.window_open === false;
    const admin = puede('canales.plantillas.gestionar');

    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            titulo="Escribir con una plantilla"
            eyebrow={fuera ? 'Fuera de las 24 h' : 'Plantilla aprobada'}
            subtitulo={`A ${nombre} por ${nombreCanal}`}
            icono="file-text"
            accion="Enviar"
            accionIcono="send"
            inactiva={plantilla === null || falta || sinSaldo}
            onAccion={() => { setConfirmar(true); }}
        >
            <div className="rv-ca-bloque">
                <h3 className="rv-bz-ceja">Plantillas aprobadas</h3>
                {plantillas === null && <p className="rv-bz-texto">Cargando…</p>}
                <div role="radiogroup" aria-label="Plantillas aprobadas" className="rv-bz-plantillas">
                    {(plantillas ?? []).map((p) => {
                        const apagada = p.category === 'marketing' && !comercial;
                        return (
                            <button
                                key={p.id}
                                type="button"
                                role="radio"
                                aria-checked={p.id === elegida}
                                aria-disabled={apagada}
                                className={`rv-bz-plantilla${apagada ? ' rv-bz-plantilla--off' : ''}`}
                                onClick={() => { if (!apagada) setElegida(p.id); }}
                            >
                                <span className="rv-bz-plantilla__punto" aria-hidden="true" />
                                <span>
                                    <span className="rv-bz-plantilla__t"><strong>{p.name}</strong>{p.category != null && CATEGORIA[p.category] !== undefined && <Chip estado="neutral">{CATEGORIA[p.category] ?? ''}</Chip>}</span>
                                    <small>{apagada ? `${primero} no ha aceptado mensajes comerciales por ${nombreCanal}.` : `${deQuien(p)} · ${idioma(p.locale)}`}</small>
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
            {plantilla !== null && huecos.length > 0 && (
                <div className="rv-ca-bloque">
                    <h3 className="rv-bz-ceja">Rellena los huecos</h3>
                    {huecos.map((h) => (
                        <Campo key={h} etiqueta={`{{${h}}}`} valor={mios[h] ?? ''} max={1024} onChange={(v) => { setValores((x) => ({ ...x, [plantilla.id]: { ...(x[plantilla.id] ?? {}), [h]: v } })); }} />
                    ))}
                </div>
            )}
            {plantilla !== null && (
                <div className="rv-ca-bloque">
                    <h3 className="rv-bz-ceja">Vista previa <small>{`Así lo recibe ${primero}`}</small></h3>
                    <p className="rv-bz-previa">
                        {cuerpoDe(plantilla).split(HUECO).map((trozo, i) =>
                            i % 2 === 1 ? (
                                (mios[trozo] ?? '').trim() === '' ? <span key={String(i)} className="rv-bz-previa__falta">{`[{{${trozo}}}]`}</span> : <strong key={String(i)}>{(mios[trozo] ?? '').trim()}</strong>
                            ) : (
                                <span key={String(i)}>{trozo}</span>
                            ),
                        )}
                    </p>
                </div>
            )}
            <p className={`rv-bz-nota${sinSaldo ? ' rv-bz-nota--error' : ''}`}>
                <Icono nombre={sinSaldo ? 'circle-alert' : 'euro'} />
                <span>{sinSaldo ? 'No te quedan mensajes de WhatsApp, ni los de tu plan ni los del paquete extra: no se puede enviar hasta que haya más.' : 'Meta cobra este mensaje y va con tu plan: no le pagas tú. Descuenta 1 de tus mensajes de WhatsApp.'}</span>
            </p>
            <p className="rv-bz-nota">
                <Icono nombre="file-text" />
                <span>
                    {admin ? '¿Te falta alguna? Crea plantillas de marketing y mira las de Reservaria en Canales. ' : '¿Te falta alguna? Las gestiona el propietario o un administrador.'}
                    {admin && <button type="button" className="rv-link" onClick={() => { navegar('/canales?apartado=plantillas'); }}>Ir a Plantillas de Meta</button>}
                </span>
            </p>
            <Ventana
                abierta={confirmar && plantilla !== null}
                titulo={`Enviar «${plantilla?.name ?? ''}»`}
                texto={`Meta cobra este mensaje y va con tu plan: descuenta 1 de tus mensajes de WhatsApp. ${primero} lo recibirá ahora.`}
                accion="Enviar"
                accionIcono="send"
                ocupado={enviando}
                ocupadoTexto="Enviando…"
                movil={movil}
                onAccion={enviar}
                onCerrar={() => { if (!enviando) setConfirmar(false); }}
            />
        </PanelLateral>
    );
}
